import { defineStore } from 'pinia'
import { ref } from 'vue'

import { authedFetch } from '../libs/auth-fetch'

/**
 * Client-side cache of the user's long-term memory + companion state, fetched
 * from the server (which persists it as a Markdown file + JSON). The chat
 * context provider reads `contextText` synchronously and injects it into the
 * LLM prompt, so 云璃 remembers across sessions.
 *
 * `recordTurn` is throttled: every ~6 messages we ask the server to extract
 * durable facts + mood drift from the recent conversation.
 */
export const useMemoryStore = defineStore('memory', () => {
  const contextText = ref('')
  const state = ref<any>(null)
  const loaded = ref(false)
  const loading = ref(false)

  async function load() {
    if (loading.value)
      return
    loading.value = true
    try {
      const res = await authedFetch('/api/v1/memory/context')
      if (res.ok) {
        const data = await res.json()
        contextText.value = data.context || ''
        loaded.value = true
      }
    }
    catch {
      // 记忆不可用不影响聊天
    }
    finally {
      loading.value = false
    }
  }

  let pending: { role: string, content: string }[] = []
  let extracting = false

  async function recordTurn(role: string, content: string) {
    const text = typeof content === 'string' ? content.trim() : ''
    if (!text)
      return
    pending.push({ role: role === 'assistant' ? 'assistant' : 'user', content: text })
    if (pending.length < 6 || extracting)
      return

    const turns = pending
    pending = []
    extracting = true
    try {
      const res = await authedFetch('/api/v1/memory/extract', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ turns }),
      })
      if (res.ok) {
        const data = await res.json()
        if (data.context)
          contextText.value = data.context
      }
    }
    catch {
      // 抽取失败静默忽略
    }
    finally {
      extracting = false
    }
  }

  return { contextText, state, loaded, load, recordTurn }
})
